/** Сжатие фото перед upload: длинная сторона ≤ maxSide, JPEG quality. */

const DEFAULT_MAX_SIDE = 1920;
const DEFAULT_QUALITY = 0.82;
/** Уже достаточно лёгкие файлы не трогаем (если и сторона не огромная). */
const SKIP_IF_UNDER_BYTES = 350 * 1024;

function replaceExtension(name: string, ext: string): string {
  const base = name.replace(/\.[^.]+$/, '');
  return `${base || 'image'}.${ext}`;
}

async function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
      'image/jpeg',
      quality,
    );
  });
}

export async function compressImageFile(
  file: File,
  options?: { maxSide?: number; quality?: number },
): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
    return file;
  }

  const maxSide = options?.maxSide ?? DEFAULT_MAX_SIDE;
  const quality = options?.quality ?? DEFAULT_QUALITY;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  try {
    const { width, height } = bitmap;
    const longest = Math.max(width, height);
    const scale = longest > maxSide ? maxSide / longest : 1;

    if (scale === 1 && file.size <= SKIP_IF_UNDER_BYTES) {
      return file;
    }

    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await canvasToJpegBlob(canvas, quality);

    // Если сжатие не выиграло — оставляем оригинал
    if (blob.size >= file.size) {
      return file;
    }

    return new File([blob], replaceExtension(file.name, 'jpg'), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } finally {
    bitmap.close();
  }
}

export async function compressImageFiles(files: File[]): Promise<File[]> {
  return Promise.all(files.map((f) => compressImageFile(f)));
}
